import { simplrOrderGetQuery, simplrOrderPostQuery } from 'api/orders/simplrOrder';
import { setLoading } from '../index';
import { setOrders, updateSelectedOrder, setCustomerGroups } from './OrdersSearchActions';
import { handleError } from 'services/ErrorService/ErrorService';
import get from 'lodash/get';

// OSF api cannot return updated order data in the cancel || refund requests
// getOrderAndUpdateStateById fetches the updated order and updates redux state
export const getOrderAndUpdateStateById =
  (simplrAccountPtrId, domain, brandName, orderId) => async (dispatch, getState) => {
    try {
      if (!simplrAccountPtrId || !orderId || !(brandName || domain)) {
        throw new Error('One of the simplrAccountPtrId, orderId, domain, brandName is undefined');
      }

      let query = {
        simplrAccountPtrId,
        orderId,
        status: 'any',
      };

      if (domain) {
        query = {
          ...query,
          domains: domain,
        };
      } else if (brandName) {
        query = {
          ...query,
          brandName,
        };
      }

      const response = await simplrOrderGetQuery('/getOrderById', query);
      if (!response || !response.length) {
        throw new Error('Unexpected empty updated order returned');
      }

      const state = getState();
      const ordersList = get(state, `tools.ordersSearch.orders`, null);

      if (!ordersList) {
        throw new Error('Cannot extract orders list from state');
      }

      // An orderId is unique per domain. If we have more then one orders in the response, it is because the
      // search occured multiple times on the same domain. Then, all orders in response are actually the same one.
      const updatedList = ordersList.map((order) => (order.orderId === response[0].orderId ? response[0] : order));

      dispatch(updateSelectedOrder(response[0]));
      dispatch(setOrders(updatedList));
    } catch (error) {
      handleError({ error, customMessage: 'Error updating the order state', showPopup: true });
    }
  };

export const cancelSimplrOrder = (simplrAccountPtrId, payload) => {
  return async (dispatch) => {
    const combinedPayload = Object.assign({}, payload, {
      simplrAccountPtrId: simplrAccountPtrId,
    });
    try {
      const responseBody = await simplrOrderPostQuery('/order', combinedPayload);
      await dispatch(
        getOrderAndUpdateStateById(
          simplrAccountPtrId,
          combinedPayload.domain,
          combinedPayload.brandName,
          combinedPayload.orderId
        )
      );

      return responseBody;
    } catch (err) {
      throw err;
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const refundSimplrOrder = (simplrAccountPtrId, payload) => {
  return async (dispatch) => {
    const combinedPayload = Object.assign({}, payload, {
      simplrAccountPtrId: simplrAccountPtrId,
    });
    try {
      const responseBody = await simplrOrderPostQuery('/issueRefund', combinedPayload);
      await dispatch(
        getOrderAndUpdateStateById(
          simplrAccountPtrId,
          combinedPayload.domain,
          combinedPayload.brandName,
          combinedPayload.orderId
        )
      );

      return responseBody;
    } catch (err) {
      throw err;
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const refundSimplrStoreCredit = (simplrAccountPtrId, orderId, payload) => {
  return async (dispatch) => {
    const combinedPayload = Object.assign({}, payload, {
      simplrAccountPtrId: simplrAccountPtrId,
    });
    try {
      const responseBody = await simplrOrderPostQuery('/issueStoreCredit', combinedPayload);
      await dispatch(
        getOrderAndUpdateStateById(simplrAccountPtrId, combinedPayload.domain, combinedPayload.brandName, orderId)
      );

      return responseBody;
    } catch (err) {
      throw err;
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const updateShipmentAddress = (simplrAccountPtrId, payload) => {
  return async (dispatch) => {
    const combinedPayload = Object.assign({}, payload, {
      simplrAccountPtrId: simplrAccountPtrId,
    });
    try {
      const responseBody = await simplrOrderPostQuery('/address', combinedPayload);
      await dispatch(
        getOrderAndUpdateStateById(
          simplrAccountPtrId,
          combinedPayload.domain,
          combinedPayload.brandName,
          combinedPayload.orderId
        )
      );

      return responseBody;
    } catch (err) {
      throw err;
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const simplrOrderGetOrdersById = (simplrAccountPtrId, query) => {
  return async (dispatch) => {
    const combinedQuery = Object.assign({}, query, {
      simplrAccountPtrId: simplrAccountPtrId,
    });
    try {
      const responseBody = await simplrOrderGetQuery('/getOrderById', combinedQuery);
      dispatch(setLoading(false, 'searchPage'));
      dispatch(setOrders(responseBody ? responseBody : []));
    } catch (err) {
      dispatch(setLoading(false, 'searchPage'));
      handleError({ error: err, customMessage: 'There was an issue loading orders!', showPopup: true });
    }
  };
};

export const simplrOrderGetOrders = (simplrAccountPtrId, query) => {
  return async (dispatch) => {
    const combinedQuery = Object.assign({}, query, {
      simplrAccountPtrId: simplrAccountPtrId,
    });
    try {
      const responseBody = await simplrOrderGetQuery('/getOrders', combinedQuery);
      dispatch(setLoading(false, 'searchPage'));
      dispatch(setOrders(responseBody));
    } catch (err) {
      dispatch(setLoading(false, 'searchPage'));
      handleError({ error: err, customMessage: 'There was an issue loading orders!', showPopup: true });
    }
  };
};

export const simplrOrderGetStaticCustomerGroups = (simplrAccountPtrId, query) => {
  return async (dispatch) => {
    const combinedQuery = Object.assign({}, query, {
      simplrAccountPtrId: simplrAccountPtrId,
    });

    try {
      const response = await simplrOrderGetQuery('/getCustomerGroups', combinedQuery);
      const { customerGroups = [] } = response;
      // filter out dynamic groups
      const staticCustomerGroups = customerGroups.filter((group) => group.type === 'STATIC');
      dispatch(setCustomerGroups(staticCustomerGroups));
    } catch (err) {
      throw err;
    }
  };
};
